import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Stack, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { cloneDeep, concat, get, set } from 'lodash';
import React, { FC, useEffect, useMemo, useState } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import MUIAccordionComponent from 'src/components/ui-component/mui-accordion/MUIAccordionComponent';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import DeleteRecord from 'src/lib/api/DeleteRecord';
import GetRecords from 'src/lib/api/GetRecords';
import { APIS } from 'src/lib/constants/apis';
import { withKostenkategorieEinsatzbereichFilter } from 'src/utils/einsatzbereichHelper';
import {
	ALS_ERLEDIGT_MARKIEREN_LABEL,
	ERLAUTERUNG_IN_KOSTEN_TITLE,
	KOSTEN_ARTEN,
	KOSTEN_POSITION,
	KOSTENTRAEGER,
	PLATZHALTER_TEXT,
} from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { ForceTrue } from 'src/lib/validation/ValidationTypes';
import PruefAssistenz from 'src/pages/private/online-antrag/antrag-components/PruefAssistenz';
import { InhaltWrapper } from 'src/pages/private/online-antrag/antrag-components/Wrapper';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
import {
	checkIfPruefAssitenzNeedsToValidate,
	getBasisDataKostenKategorie,
	getKostenErlauterung,
} from 'src/pages/private/online-antrag/application-helper-function/AppHelperfunctions';
import { checkIfSpecificKostenPositionHasComment } from 'src/pages/private/online-antrag/application-helper-function/PruefAssistenzHelperFunktion';
import { DisplayDecimal, DisplayEuro } from 'src/utils/formats';
import { getFormStrucComponentTable } from 'src/utils/formStruc';
import { addIndexesToPath, checkReadOnlyInUeberarbeitung, enumT, getOptionsFromRes, removeDataKeys, removeNullDeep } from 'src/utils/helper';

// import schemas
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
import KostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
const P = AntragSchema.attributes;
const KP_P = KostenPositionSchema.attributes;

const EinnahmenEPZ: FC<{ hasComment?: boolean }> = ({ hasComment }) => {
	const { application, setApplicationProperty, saveApplication, readOnly } = useApplication();
	const [kostenKategorie, setKostenKategorie] = useState([]);
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const handleAddEinnahmen = async () => {
		let appCopy = cloneDeep(application);

		const data = getBasisDataKostenKategorie(
			appCopy,
			kostenKategorie, // All kostenkategorie for Eigenmittel, to find default Kostenkategorie id
			KOSTENTRAEGER.Antragsteller,
			get(application, `${P.Antragstellende_Organisation.path}.id`),
			KOSTEN_POSITION.Einnahme,
			KOSTEN_ARTEN.Eigenmittel,
		);
		set(
			appCopy,
			addIndexesToPath(
				P.Foerderantrag_Kostenpositions.path,
				[`${get(application, P.Foerderantrag_Kostenpositions.path, []).length}`],
				['Foerderantrag_Kostenpositions'],
			),
			{ attributes: data },
		);
		saveApplication(false, appCopy);
	};
	const handleDeleteEinnahmen = async (id: number) => {
		try {
			let appcopy = cloneDeep(application);
			const allKostenposition = get(appcopy, P.Foerderantrag_Kostenpositions.path, []);
			set(
				appcopy,
				P.Foerderantrag_Kostenpositions.path,
				allKostenposition.filter((item: any) => item.id !== id),
			);
			let res = await DeleteRecord(APIS.Kostenposition, id);
			if (res) {
				saveApplication(false, appcopy);
			}
		} catch (err: any) {
			console.error('ERROR: Deleting Personalkosten', err);
		}
	};

	const ausgabeSumme = useMemo(() => {
		const allKostenPositionPartnerFiltered = get(application, P.Foerderantrag_Kostenpositions.path, []).filter(
			(ele: any) => get(ele, KP_P.Allgemein_Kostenposition.path, '') === KOSTEN_POSITION.Ausgabe,
		);
		if (allKostenPositionPartnerFiltered.length > 0) {
			return allKostenPositionPartnerFiltered.reduce((sum: any, ele: any) => {
				return sum + parseFloat(get(ele, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0.0'));
			}, 0);
		}
		return 0;
	}, [application]);
	const einnahmeSumme = useMemo(() => {
		const allKostenPositionPartnerFiltered = get(application, P.Foerderantrag_Kostenpositions.path, []).filter(
			(ele: any) => get(ele, KP_P.Allgemein_Kostenposition.path, '') === KOSTEN_POSITION.Einnahme,
		);
		if (allKostenPositionPartnerFiltered.length > 0) {
			return allKostenPositionPartnerFiltered.reduce((sum: any, ele: any) => {
				return sum + parseFloat(get(ele, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0.0'));
			}, 0);
		}
		return 0;
	}, [application]);
	const foerderquote = useMemo(() => {
		const foerderQuote = Number(ausgabeSumme) > 0 ? ((ausgabeSumme - einnahmeSumme) / ausgabeSumme) * 100 : 0;
		return foerderQuote;
	}, [ausgabeSumme, einnahmeSumme]);

	useEffect(() => {
		const fetch = async () => {
			try {
				const filter = withKostenkategorieEinsatzbereichFilter({
					Allgemein_Kostenposition: KOSTEN_POSITION.Einnahme,
					Allgemein_Kostenart_Finanzierung: KOSTEN_ARTEN.Eigenmittel,
					Allgemein_Antragsformular: {
						$contains: get(application, P.Allgemein_Antragsformular.path),
					},
					Allgemein_Aktiv: { $eq: true },
				}, APIS.Antrag);
				let res = await GetRecords(APIS.Kostenkategorie, [], filter);
				setKostenKategorie(removeNullDeep(removeDataKeys(get(res, 'data', []))));
			} catch (err: any) {
				console.error('ERROR: FEtching Kosten kategorie ', err);
			}
		};
		fetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<ApplicationHeading title={enumT(KOSTEN_ARTEN.Eigenmittel)} description={PLATZHALTER_TEXT} />

			<InhaltWrapper marginTop='0'>
				<Typography>
					Einnahmearten - auch beantragte aber noch nicht bewilligte Förderungen Dritter - sind vollständig aufzuführen, ggf. sind weitere Zeilen
					zu ergänzen. Die Summen der Einnahmen pro Haushaltsjahr im Finanzierungsplan müssen den Gesamtausgaben pro Haushaltsjahr im
					Ausgabenplan entsprechen.
				</Typography>
				<Typography>
					Einnahmearten sind u.a. öffentliche Fördermittel von EU, Bund, Land oder Kommunen, sonstige Drittmittel, Eigenmittel sowie Einnahmen
					aus Projektaktivitäten.
				</Typography>
				<Typography>
					Die Förderquote im Rahmen der Entwicklungspolitischen Zusammenarbeit des Landes Hessen beträgt i.d.R. bis zu 40 %.
				</Typography>
				<Typography>
					Bitte verteilen Sie Ihre Eigen- und Drittmittel so, dass Sie bei mehrjährigen Projekten möglichst wenig oder keine Förderung im zweiten
					Projektjahr beantragen, wenn das Projekt bis zum 31.03. (Inland) bzw. 30.06. (Ausland) geht.
				</Typography>
			</InhaltWrapper>
			<Box sx={{ mt: '1rem' }}>
				<MUIAlert
					title={`Gesamtausgaben des ${KOSTENTRAEGER.Antragsteller}s: ${DisplayEuro(ausgabeSumme)}`}
					message={`Berechnete Förderquote dieses  ${KOSTENTRAEGER.Antragsteller}s: ${DisplayDecimal(foerderquote)} % `}
				/>
				{ausgabeSumme <= einnahmeSumme && (
					<MUIAlert
						infoType={'error'}
						message={`Ihre Eigenmittel liegen über den Gesamtausgaben dieses ${KOSTENTRAEGER.Antragsteller}s. Einnahmen gesamt: ${DisplayEuro(einnahmeSumme)}`}
					/>
				)}
			</Box>

			{/* eslint-disable-next-line array-callback-return*/}
			{get(application, P.Foerderantrag_Kostenpositions.path, []).map((eachKP: any, i: number) => {
				if (
					get(eachKP, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Einnahme &&
					get(eachKP, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Eigenmittel
				) {
					return (
						<MUIAccordionComponent
							key={i}
							summaryTitle={`${enumT(KOSTEN_ARTEN.Eigenmittel)} - ${get(eachKP, KP_P.Allgemein_Bezeichnung.path, '')}`}
							onDelete={() => handleDeleteEinnahmen(get(eachKP, 'id'))}
							showDeleteButton={!updatedReadOnly}
							hasComment={checkIfSpecificKostenPositionHasComment(readOnly, eachKP)}
						>
							<PruefAssistenz
								statusPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Pruefstatus.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								hinweisPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Hinweis_Fuer_Antragsteller.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								ueberarbeitetAmPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Ueberarbeitet_Am.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								erledigtPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Erledigt.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								formStruc={mapKosten(PruefAssistenzStruc(), i)}
							/>

							<MUIAlert
								title={ERLAUTERUNG_IN_KOSTEN_TITLE}
								message={getKostenErlauterung(kostenKategorie, get(eachKP, KP_P.Allgemein_Kostenkategorie.path))}
							/>

							<Grid2 spacing={6} container>
								<Grid2 sm={6}>
									<CreateForm
										formStruc={mapKosten(formStrucGeneral(kostenKategorie), i)}
										formValues={application}
										updateFormValuesFunc={setApplicationProperty}
										readOnly={updatedReadOnly}
										api={APIS.Kostenposition}
										size='small'
									/>
								</Grid2>
								<Grid2 sm={6}>
									<CreateForm
										formStruc={mapKosten(kostenProJahrStruc(application, i), i)}
										formValues={application}
										updateFormValuesFunc={setApplicationProperty}
										readOnly={updatedReadOnly}
										api={APIS.Kostenposition}
									/>
								</Grid2>
							</Grid2>
						</MUIAccordionComponent>
					);
				}
			})}
			<Stack alignItems={'flex-end'}>
				<Button sx={{ mt: '2rem', maxWidth: 'fit-content' }} endIcon={<AddIcon />} onClick={handleAddEinnahmen} disabled={updatedReadOnly}>
					{enumT(KOSTEN_ARTEN.Eigenmittel)} hinzufügen
				</Button>
			</Stack>
		</>
	);
};

export default EinnahmenEPZ;

const PruefAssistenzStruc = () => {
	return [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];
};
const formStrucGeneral = (kostenKategorie?: any[]) => {
	const kategorieOptions = kostenKategorie || [];
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kostenkategorie,
					type: 'singleSelect',
					options: getOptionsFromRes(kategorieOptions, 'Allgemein_Bezeichnung'),
					label: 'Kategorie',
				},
				{ ...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Bezeichnung },
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const kostenProJahrStruc = (application: any, i: number) => [
	{
		fields: [
			{
				...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kosten_Pro_Jahr,
				fieldProps: { Zelle_2: { format: 'currency' } },
				disabledColumns: ['Zelle_1'],
				type: 'componentTable',
				tableLabel: 'Betrag pro Jahr',
				maxLevel: get(application, P.Allgemein_Projektdauerin_Jahren.path),
				hideDeleteButton: true,
				sumRow: [
					{
						id: 'pinnedRow',
						Zelle_1: 'Gesamt',
						Zelle_2: get(
							application,
							addIndexesToPath(
								P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Geplante_Gesamtsumme.path,
								[`${i}`],
								['Foerderantrag_Kostenpositions'],
							),
						),
					},
				],
			},
		],
		userProps: { columnToDivide: 1 },
	},
];

const mapKosten = (formStruc: FormStrucItem[], index: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				return {
					...value,
					path: addIndexesToPath(get(value, 'path', ''), [`${index}`], ['Foerderantrag_Kostenpositions']),
				};
			}),
		};
	});
	return result;
};
export const getFormStrucEinnahmenEPZ = (application: any) => {
	let result: FormStrucItem[] = [];

	get(application, P.Foerderantrag_Kostenpositions.path, []).forEach((eachKP: any, i: number) => {
		if (
			get(eachKP, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Einnahme &&
			get(eachKP, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Eigenmittel
		) {
			const isHinweisFieldRequired = checkIfPruefAssitenzNeedsToValidate(eachKP, KP_P.Pruefung_Pruefstatus.path, KP_P.Pruefung_Erledigt.path);
			const formStrucGeneralWithIndex = mapKosten(formStrucGeneral(), i);
			const formStrucCTWithIndex = getFormStrucComponentTable(application, mapKosten(kostenProJahrStruc(application, i), i));

			if (isHinweisFieldRequired) {
				result = concat(mapKosten(PruefAssistenzStruc(), i), result, formStrucGeneralWithIndex, formStrucCTWithIndex);
			} else {
				result = concat(result, formStrucGeneralWithIndex, formStrucCTWithIndex);
			}
		}
	});

	return result;
};
